import {
  ITextBoxesDisplay,
  ITextBoxesEvidence,
  ITextBoxesSignal,
  TextBoxesBbox,
  TextBoxesColor
} from 'src/ts/interfaces/textBoxes';

export type KindColor = TextBoxesColor | 'neutral';

export interface KindStyle {
  kind: string;
  color: KindColor;
  headline: string;
  description: string;
  note?: string;
  note_at?: 'before' | 'after';
}

export interface BoxMark {
  kind: string;
  rule: boolean;
  signal: ITextBoxesSignal;
}

export interface Piece {
  bbox: TextBoxesBbox;
  text: string;
  marks: BoxMark[];
}

export interface Orphan {
  bbox: TextBoxesBbox;
  mark: BoxMark;
}

export interface PageModel {
  page: number;
  width: number;
  height: number;
  pieces: Piece[];
  orphans: Orphan[];
}

export interface LegendEntry extends KindStyle {
  count: number;
  sample: string;
  removed: boolean;
}

export interface Chip {
  bbox: TextBoxesBbox;
  text: string;
  mark: BoxMark | null;
  orphan: boolean;
}

export interface Row {
  page: number;
  chips: Chip[];
  mark: BoxMark | null;
}

export interface TextBoxesModel {
  providerName: string;
  font: 'mono' | 'sans';
  pages: PageModel[];
  legend: LegendEntry[];
  normalSample: string;
  alsoFound: string[];
  kindStyle: (kind: string) => KindStyle;
}

const SAMPLE_LENGTH = 14;
const ROW_TOLERANCE = 2;
const COVER_SHARE = 0.5;

export const noteFor = (style: KindStyle, signal: ITextBoxesSignal): string | null => {
  if (!style.note) return null;
  const values = signal.values ?? {};
  return style.note
    .replace(/\{(\w+)\}/g, (_, field: string) =>
      values[field] === undefined ? '' : values[field].toLocaleString('en-US')
    )
    .replace(/\s+/g, ' ')
    .trim();
};

const area = ([x0, y0, x1, y1]: TextBoxesBbox) => Math.max(x1 - x0, 0) * Math.max(y1 - y0, 0);

export const covers = (box: TextBoxesBbox, piece: TextBoxesBbox): boolean => {
  const overlap = area([
    Math.max(box[0], piece[0]),
    Math.max(box[1], piece[1]),
    Math.min(box[2], piece[2]),
    Math.min(box[3], piece[3])
  ]);
  const pieceArea = area(piece);
  if (pieceArea === 0) {
    const cx = (piece[0] + piece[2]) / 2;
    const cy = (piece[1] + piece[3]) / 2;
    return cx >= box[0] && cx <= box[2] && cy >= box[1] && cy <= box[3];
  }
  return overlap / pieceArea >= COVER_SHARE;
};

export const blankText = (text: string) => '·'.repeat(Math.min(Math.max(text.length, 3), 10));

const clip = (text: string) =>
  text.length > SAMPLE_LENGTH ? `${text.slice(0, SAMPLE_LENGTH - 1)}…` : text;

export const primaryMark = (marks: BoxMark[]): BoxMark | null => marks[0] ?? null;

export const buildModel = (
  evidence: ITextBoxesEvidence,
  display: ITextBoxesDisplay
): TextBoxesModel => {
  const kindOrder = Object.keys(display.kinds);
  const rank = (mark: BoxMark) => {
    const index = kindOrder.indexOf(mark.kind);
    return (mark.rule ? 0 : kindOrder.length + 1) + (index === -1 ? kindOrder.length : index);
  };
  const kindStyle = (kind: string): KindStyle => {
    const style = display.kinds[kind];
    return style ? { kind, ...style } : { kind, color: 'neutral', headline: kind, description: '' };
  };

  const alsoFound = (evidence.also_found ?? [])
    .map((name) => display.also_found[name])
    .filter((label): label is string => Boolean(label));
  const counts = new Map<string, number>();

  const pages: PageModel[] = (evidence.pages ?? []).map((source) => {
    const onPage: BoxMark[] = (source.signals ?? []).map((signal) => ({
      kind: signal.type,
      rule: signal.role === 'rule',
      signal
    }));
    onPage.forEach((mark) => counts.set(mark.kind, (counts.get(mark.kind) ?? 0) + 1));
    const used = new Set<BoxMark>();
    const pieces = source.text.map((piece) => {
      const covering = onPage
        .filter((mark) => covers(mark.signal.bbox, piece.bbox))
        .sort((a, b) => rank(a) - rank(b));
      covering.forEach((mark) => used.add(mark));
      return { bbox: piece.bbox, text: piece.text, marks: covering };
    });
    const orphans = onPage
      .filter((mark) => !used.has(mark))
      .map((mark) => ({ bbox: mark.signal.bbox, mark }));
    return { page: source.number, width: source.width, height: source.height, pieces, orphans };
  });

  const allPieces = pages.flatMap((page) => page.pieces);
  const sampleFor = (kind: string) => {
    const piece = allPieces.find((p) => p.marks.some((mark) => mark.kind === kind));
    if (!piece) return '';
    return piece.text.trim() ? clip(piece.text.trim()) : blankText(piece.text);
  };
  const normal = allPieces.find((p) => p.marks.length === 0 && p.text.trim());

  const order = [...kindOrder, ...Array.from(counts.keys()).filter((k) => !display.kinds[k])];
  const legend = order
    .filter((kind) => counts.has(kind))
    .map((kind) => {
      const sample = sampleFor(kind);
      return { ...kindStyle(kind), count: counts.get(kind) ?? 0, sample, removed: !sample };
    });

  return {
    providerName: display.provider?.name ?? evidence.template,
    font: display.provider?.font ?? 'sans',
    pages,
    legend,
    normalSample: normal ? clip(normal.text.trim()) : '',
    alsoFound,
    kindStyle
  };
};

export const flaggedOn = (page: PageModel): number =>
  page.pieces.filter((piece) => piece.marks.length > 0).length + page.orphans.length;

export const rowsOf = (page: PageModel): Row[] => {
  const chips: Chip[] = [
    ...page.pieces.map((piece) => ({
      bbox: piece.bbox,
      text: piece.text,
      mark: primaryMark(piece.marks),
      orphan: false
    })),
    ...page.orphans.map((orphan) => ({
      bbox: orphan.bbox,
      text: '',
      mark: orphan.mark,
      orphan: true
    }))
  ].sort((a, b) => a.bbox[3] - b.bbox[3] || a.bbox[0] - b.bbox[0]);

  const rows: { bottom: number; chips: Chip[] }[] = [];
  chips.forEach((chip) => {
    const last = rows[rows.length - 1];
    if (last && Math.abs(chip.bbox[3] - last.bottom) <= ROW_TOLERANCE) last.chips.push(chip);
    else rows.push({ bottom: chip.bbox[3], chips: [chip] });
  });

  return rows.map((row) => {
    const sorted = row.chips.sort((a, b) => a.bbox[0] - b.bbox[0]);
    return {
      page: page.page,
      chips: sorted,
      mark: sorted.find((chip) => chip.mark)?.mark ?? null
    };
  });
};
